import type { ComponentSchema, TemplateSchema } from '@ptd/react-designer'

const UI_FONT = "'Sarasa UI SC', 'Microsoft YaHei UI', sans-serif"
const SERIF_FONT = "'Noto Serif SC', 'Source Han Serif SC', serif"

function component(
  id: string,
  type: ComponentSchema['component'],
  name: string,
  propValue: unknown,
  style: ComponentSchema['style'],
  group: ComponentSchema['group'] = 'common',
): ComponentSchema {
  return {
    id,
    component: type,
    name,
    code: type,
    group,
    propValue,
    style,
    groupStyle: {},
    position: { x: style.left, y: style.top },
  }
}

export const INITIAL_TEMPLATE: TemplateSchema = {
  _version: 1,
  pageConfig: {
    pageSize: 'A4',
    pageDirection: 'p',
    pageLayout: 'fixed',
    pageWidth: 210,
    pageHeight: 297,
    pageCurHeight: 297,
    pageMarginBottom: 10,
    pageMarginTop: 10,
    pageMarginLeft: 10,
    pageMarginRight: 10,
    title: '新建模板',
    scale: 1,
    background: '#fcfdff',
    color: '#1d2735',
    fontSize: 12,
    fontFamily: SERIF_FONT,
    lineHeight: 1.4,
  },
  pages: [{ id: 'page-1', componentData: [] }],
  dataSource: [],
  dataSet: {},
}

/**
 * A deterministic real document used only by the DEV product capture route.
 * It deliberately uses the same schema and renderers as ordinary documents.
 */
export const PRODUCT_CAPTURE_TEMPLATE: TemplateSchema = {
  _version: 1,
  pageConfig: {
    pageSize: 'custom',
    pageDirection: 'p',
    pageLayout: 'fixed',
    pageWidth: 148,
    pageHeight: 210,
    pageCurHeight: 210,
    pageMarginBottom: 8,
    pageMarginTop: 8,
    pageMarginLeft: 8,
    pageMarginRight: 8,
    title: '冷链出库标签 · 华东 07',
    scale: 1,
    background: '#fcfdff',
    color: '#1d2735',
    fontSize: 10,
    fontFamily: UI_FONT,
    lineHeight: 1.35,
  },
  pages: [
    {
      id: 'capture-page-1',
      componentData: [
        component(
          'capture-band',
          'RoyRect',
          '标题底色',
          null,
          {
            left: 31,
            top: 34,
            width: 496,
            height: 76,
            rotate: 0,
            opacity: 1,
            background: '#1d2735',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#1d2735',
            borderRadius: '0',
          },
          'shape',
        ),
        component('capture-kicker', 'RoySimpleText', '业务类型', 'OUTBOUND / 冷链出库', {
          left: 50,
          top: 45,
          width: 230,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#b9c9e8',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
          letterSpacing: '1.2px',
        }),
        component('capture-title', 'RoySimpleText', '标签标题', '华东冷链出库标签', {
          left: 50,
          top: 68,
          width: 330,
          height: 34,
          rotate: 0,
          opacity: 1,
          color: '#f8fafc',
          fontFamily: SERIF_FONT,
          fontSize: 22,
          fontWeight: '600',
          letterSpacing: '0.4px',
        }),
        component('capture-proof', 'RoySimpleText', '校样标记', '已校样', {
          left: 435,
          top: 57,
          width: 72,
          height: 31,
          rotate: 0,
          opacity: 1,
          color: '#f8fafc',
          background: '#c84f3e',
          fontFamily: UI_FONT,
          fontSize: 11,
          fontWeight: '600',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '6px 8px',
        }),
        component('capture-order-label', 'RoySimpleText', '订单标签', '出库单号', {
          left: 50,
          top: 135,
          width: 90,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#627084',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
        }),
        component('capture-order', 'RoySimpleText', '订单编号', 'FQ-20260731-0842', {
          left: 50,
          top: 158,
          width: 290,
          height: 34,
          rotate: 0,
          opacity: 1,
          color: '#1d2735',
          fontFamily: UI_FONT,
          fontSize: 19,
          fontWeight: '600',
          letterSpacing: '0.5px',
        }),
        component(
          'capture-qr',
          'RoyQRCode',
          '追溯二维码',
          {
            text: 'https://example.com/trace/FQ-20260731-0842',
            colorDark: '#1d2735',
            colorLight: '#ffffff',
            correctLevel: 'M',
            margin: 2,
          },
          { left: 409, top: 130, width: 98, height: 98, rotate: 0, opacity: 1 },
        ),
        component(
          'capture-rule-1',
          'RoyLine',
          '分隔线',
          null,
          {
            left: 50,
            top: 213,
            width: 328,
            height: 1,
            rotate: 0,
            opacity: 1,
            background: '#b8c2ce',
          },
          'shape',
        ),
        component('capture-destination-label', 'RoySimpleText', '目的地标签', '目的地', {
          left: 50,
          top: 237,
          width: 74,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#627084',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
        }),
        component('capture-destination', 'RoySimpleText', '目的地', '上海 · 浦东新区', {
          left: 50,
          top: 260,
          width: 230,
          height: 30,
          rotate: 0,
          opacity: 1,
          color: '#1d2735',
          fontFamily: SERIF_FONT,
          fontSize: 17,
          fontWeight: '600',
        }),
        component('capture-route-label', 'RoySimpleText', '路由标签', '路线 / 温区', {
          left: 310,
          top: 237,
          width: 90,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#627084',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
        }),
        component('capture-route', 'RoySimpleText', '路由', 'HD-07  /  2–8°C', {
          left: 310,
          top: 260,
          width: 197,
          height: 30,
          rotate: 0,
          opacity: 1,
          color: '#1d2735',
          fontFamily: UI_FONT,
          fontSize: 14,
          fontWeight: '600',
        }),
        component(
          'capture-note-box',
          'RoyRect',
          '操作说明底框',
          null,
          {
            left: 50,
            top: 316,
            width: 457,
            height: 116,
            rotate: 0,
            opacity: 1,
            background: '#f2f5f8',
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#c8d0da',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'capture-note',
          'RoyText',
          '操作说明',
          '<p><strong>操作提示</strong></p><p>到货后请立即核验箱体温度与封签编号。异常件进入复核区，不得直接入库。</p>',
          {
            left: 68,
            top: 331,
            width: 420,
            height: 84,
            rotate: 0,
            opacity: 1,
            color: '#334154',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '1.5',
          },
        ),
        component(
          'capture-barcode',
          'RoyBarCode',
          '出库条形码',
          {
            text: 'FQ202607310842',
            bcid: 'code128',
            colorDark: '#1d2735',
            includeText: true,
          },
          { left: 72, top: 474, width: 413, height: 88, rotate: 0, opacity: 1 },
        ),
        component(
          'capture-footer-rule',
          'RoyLine',
          '页脚分隔线',
          null,
          {
            left: 50,
            top: 596,
            width: 457,
            height: 1,
            rotate: 0,
            opacity: 1,
            background: '#b8c2ce',
          },
          'shape',
        ),
        component('capture-footer', 'RoySimpleText', '页脚', 'Foliq · 结构化文档校样', {
          left: 50,
          top: 618,
          width: 240,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#627084',
          fontFamily: UI_FONT,
          fontSize: 9,
          letterSpacing: '0.6px',
        }),
        component('capture-page-number', 'RoySimpleText', '页码', '01 / 02', {
          left: 435,
          top: 618,
          width: 72,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#334154',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
          justifyContent: 'flex-end',
        }),
      ],
    },
    {
      id: 'capture-page-2',
      componentData: [
        component('capture-page-2-title', 'RoySimpleText', '复核记录', '出库复核记录', {
          left: 50,
          top: 50,
          width: 300,
          height: 40,
          rotate: 0,
          opacity: 1,
          color: '#1d2735',
          fontFamily: SERIF_FONT,
          fontSize: 22,
          fontWeight: '600',
        }),
      ],
    },
  ],
  dataSource: [],
  dataSet: {},
}

const DELIVERY_NOTE_CAPTURE_TEMPLATE: TemplateSchema = {
  _version: 1,
  pageConfig: {
    pageSize: 'A4',
    pageDirection: 'p',
    pageLayout: 'fixed',
    pageWidth: 210,
    pageHeight: 297,
    pageCurHeight: 297,
    pageMarginBottom: 10,
    pageMarginTop: 10,
    pageMarginLeft: 10,
    pageMarginRight: 10,
    title: '采购送货单 · SH-240731',
    scale: 1,
    background: '#fffefb',
    color: '#20272f',
    fontSize: 10,
    fontFamily: UI_FONT,
    lineHeight: 1.4,
  },
  pages: [
    {
      id: 'delivery-page-1',
      componentData: [
        component(
          'delivery-header',
          'RoyRect',
          '标题栏',
          null,
          {
            left: 48,
            top: 42,
            width: 690,
            height: 74,
            rotate: 0,
            opacity: 1,
            background: '#223042',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#223042',
            borderRadius: '0',
          },
          'shape',
        ),
        component('delivery-kicker', 'RoySimpleText', '单据类型', 'PURCHASE / DELIVERY', {
          left: 70,
          top: 57,
          width: 220,
          height: 18,
          rotate: 0,
          opacity: 1,
          color: '#a9bbd1',
          fontFamily: UI_FONT,
          fontSize: 8,
          fontWeight: '700',
          letterSpacing: '1.3px',
        }),
        component('delivery-title', 'RoySimpleText', '标题', '采购送货单', {
          left: 70,
          top: 76,
          width: 280,
          height: 32,
          rotate: 0,
          opacity: 1,
          color: '#f8fafc',
          fontFamily: SERIF_FONT,
          fontSize: 21,
          fontWeight: '600',
        }),
        component('delivery-number', 'RoySimpleText', '送货单号', 'NO. SH-240731-018', {
          left: 470,
          top: 75,
          width: 238,
          height: 24,
          rotate: 0,
          opacity: 1,
          color: '#f8fafc',
          fontFamily: UI_FONT,
          fontSize: 11,
          fontWeight: '600',
          justifyContent: 'flex-end',
        }),
        component('delivery-supplier-label', 'RoySimpleText', '供应商标签', '供应商', {
          left: 70,
          top: 146,
          width: 80,
          height: 18,
          rotate: 0,
          opacity: 1,
          color: '#6a7685',
          fontFamily: UI_FONT,
          fontSize: 8,
          fontWeight: '700',
        }),
        component('delivery-supplier', 'RoySimpleText', '供应商', '苏州恒准精密器件有限公司', {
          left: 70,
          top: 166,
          width: 310,
          height: 28,
          rotate: 0,
          opacity: 1,
          color: '#20272f',
          fontFamily: SERIF_FONT,
          fontSize: 15,
          fontWeight: '600',
        }),
        component('delivery-date-label', 'RoySimpleText', '交付日期标签', '交付日期 / 仓库', {
          left: 455,
          top: 146,
          width: 160,
          height: 18,
          rotate: 0,
          opacity: 1,
          color: '#6a7685',
          fontFamily: UI_FONT,
          fontSize: 8,
          fontWeight: '700',
        }),
        component('delivery-date', 'RoySimpleText', '交付日期', '2026-07-31  /  华东一号仓', {
          left: 455,
          top: 166,
          width: 253,
          height: 28,
          rotate: 0,
          opacity: 1,
          color: '#20272f',
          fontFamily: UI_FONT,
          fontSize: 12,
          fontWeight: '600',
        }),
        component(
          'delivery-table-head',
          'RoyRect',
          '明细表头',
          null,
          {
            left: 70,
            top: 224,
            width: 638,
            height: 36,
            rotate: 0,
            opacity: 1,
            background: '#e8edf2',
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#9ca8b5',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'delivery-table-labels',
          'RoySimpleText',
          '明细表头文字',
          '序号      物料名称 / 规格                         数量       单位       批次',
          {
            left: 85,
            top: 233,
            width: 605,
            height: 20,
            rotate: 0,
            opacity: 1,
            color: '#334154',
            fontFamily: UI_FONT,
            fontSize: 9,
            fontWeight: '700',
          },
        ),
        component(
          'delivery-rows',
          'RoyText',
          '送货明细',
          '<p>01　食品级保温箱 / 42L　　　　　　　　　24　　箱　　B240731A</p><p>02　温度记录卡 / T-08　　　　　　　　　　48　　枚　　B240729C</p><p>03　防拆封签 / 蓝色 120mm　　　　　　　 96　　枚　　B240730F</p><p>04　吸水衬垫 / 400 × 300　　　　　　　　 240　 张　　B240728D</p>',
          {
            left: 84,
            top: 274,
            width: 610,
            height: 194,
            rotate: 0,
            opacity: 1,
            color: '#263240',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '2.2',
          },
        ),
        component(
          'delivery-row-lines',
          'RoyRect',
          '明细外框',
          null,
          {
            left: 70,
            top: 260,
            width: 638,
            height: 222,
            rotate: 0,
            opacity: 1,
            background: 'transparent',
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#9ca8b5',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'delivery-note-box',
          'RoyRect',
          '交付说明底框',
          null,
          {
            left: 70,
            top: 516,
            width: 638,
            height: 104,
            rotate: 0,
            opacity: 1,
            background: '#f5f6f4',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#d4d9df',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'delivery-note',
          'RoyText',
          '交付说明',
          '<p><strong>交付要求</strong></p><p>送达后由仓管员核对批次、数量和外包装。温控物料须附最近一次校准记录。</p>',
          {
            left: 88,
            top: 533,
            width: 600,
            height: 72,
            rotate: 0,
            opacity: 1,
            color: '#3d4855',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '1.6',
          },
        ),
        component(
          'delivery-summary-box',
          'RoyRect',
          '汇总底框',
          null,
          {
            left: 70,
            top: 646,
            width: 638,
            height: 92,
            rotate: 0,
            opacity: 1,
            background: '#eef2f4',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#c8d0d8',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'delivery-summary',
          'RoyText',
          '送货汇总',
          '<p><strong>本单合计</strong>　4 个物料批次　/　408 件</p><p>随货文件：出厂检验单、温度记录卡校准证明</p>',
          {
            left: 90,
            top: 662,
            width: 410,
            height: 62,
            rotate: 0,
            opacity: 1,
            color: '#344252',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '1.7',
          },
        ),
        component('delivery-status', 'RoySimpleText', '收货状态', '待签收', {
          left: 585,
          top: 666,
          width: 96,
          height: 40,
          rotate: 0,
          opacity: 1,
          color: '#f8fafc',
          background: '#c65a3b',
          fontFamily: UI_FONT,
          fontSize: 12,
          fontWeight: '700',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 10px',
        }),
        component(
          'delivery-sign',
          'RoySimpleText',
          '签收栏',
          '送货人签字　　　　　　　　　仓管员签字　　　　　　　　　日期',
          {
            left: 70,
            top: 782,
            width: 638,
            height: 24,
            rotate: 0,
            opacity: 1,
            color: '#5d6976',
            fontFamily: UI_FONT,
            fontSize: 9,
          },
        ),
        component(
          'delivery-footer',
          'RoySimpleText',
          '页脚',
          'FOLIQ / PURCHASE DELIVERY NOTE · 01',
          {
            left: 70,
            top: 1020,
            width: 638,
            height: 18,
            rotate: 0,
            opacity: 1,
            color: '#7a8591',
            fontFamily: UI_FONT,
            fontSize: 8,
            letterSpacing: '0.8px',
          },
        ),
      ],
    },
  ],
  dataSource: [],
  dataSet: {},
}

const PRICE_LABEL_CAPTURE_TEMPLATE: TemplateSchema = {
  _version: 1,
  pageConfig: {
    pageSize: 'custom',
    pageDirection: 'p',
    pageLayout: 'fixed',
    pageWidth: 100,
    pageHeight: 60,
    pageCurHeight: 60,
    pageMarginBottom: 4,
    pageMarginTop: 4,
    pageMarginLeft: 4,
    pageMarginRight: 4,
    title: '门店商品价签 · 精品咖啡豆',
    scale: 1,
    background: '#fffdf7',
    color: '#1f2721',
    fontSize: 10,
    fontFamily: UI_FONT,
    lineHeight: 1.35,
  },
  pages: [
    {
      id: 'price-label-page-1',
      componentData: [
        component(
          'price-accent',
          'RoyRect',
          '品牌色块',
          null,
          {
            left: 28,
            top: 28,
            width: 88,
            height: 218,
            rotate: 0,
            opacity: 1,
            background: '#126b4f',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#126b4f',
            borderRadius: '0',
          },
          'shape',
        ),
        component('price-store', 'RoySimpleText', '门店品牌', 'MORNING / 07', {
          left: 45,
          top: 42,
          width: 58,
          height: 48,
          rotate: 0,
          opacity: 1,
          color: '#d8f0e5',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '700',
          letterSpacing: '1px',
        }),
        component('price-category', 'RoySimpleText', '品类', 'COFFEE', {
          left: 47,
          top: 202,
          width: 60,
          height: 20,
          rotate: -90,
          opacity: 1,
          color: '#d8f0e5',
          fontFamily: UI_FONT,
          fontSize: 8,
          fontWeight: '700',
          letterSpacing: '2px',
        }),
        component('price-name', 'RoySimpleText', '商品名称', '云南日晒精品咖啡豆', {
          left: 132,
          top: 39,
          width: 300,
          height: 34,
          rotate: 0,
          opacity: 1,
          color: '#1f2721',
          fontFamily: SERIF_FONT,
          fontSize: 19,
          fontWeight: '600',
        }),
        component('price-spec', 'RoySimpleText', '商品规格', '中度烘焙 · 250g / 袋', {
          left: 132,
          top: 79,
          width: 288,
          height: 22,
          rotate: 0,
          opacity: 1,
          color: '#68726b',
          fontFamily: UI_FONT,
          fontSize: 9,
          fontWeight: '600',
        }),
        component('price-currency', 'RoySimpleText', '货币符号', '¥', {
          left: 129,
          top: 116,
          width: 28,
          height: 35,
          rotate: 0,
          opacity: 1,
          color: '#1f2721',
          fontFamily: UI_FONT,
          fontSize: 18,
          fontWeight: '700',
        }),
        component('price-value', 'RoySimpleText', '价格', '68.00', {
          left: 157,
          top: 104,
          width: 176,
          height: 62,
          rotate: 0,
          opacity: 1,
          color: '#1f2721',
          fontFamily: UI_FONT,
          fontSize: 38,
          fontWeight: '700',
          letterSpacing: '-1px',
        }),
        component('price-member', 'RoySimpleText', '会员提示', '会员价 ¥59.00', {
          left: 135,
          top: 179,
          width: 165,
          height: 25,
          rotate: 0,
          opacity: 1,
          color: '#fffdf7',
          background: '#d35c37',
          fontFamily: UI_FONT,
          fontSize: 10,
          fontWeight: '700',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4px 8px',
        }),
        component(
          'price-barcode',
          'RoyBarCode',
          '商品条码',
          {
            text: '6972026073108',
            bcid: 'code128',
            colorDark: '#1f2721',
            includeText: true,
          },
          { left: 316, top: 169, width: 128, height: 68, rotate: 0, opacity: 1 },
        ),
      ],
    },
  ],
  dataSource: [],
  dataSet: {},
}

const INSPECTION_REPORT_CAPTURE_TEMPLATE: TemplateSchema = {
  _version: 1,
  pageConfig: {
    pageSize: 'A4',
    pageDirection: 'p',
    pageLayout: 'fixed',
    pageWidth: 210,
    pageHeight: 297,
    pageCurHeight: 297,
    pageMarginBottom: 10,
    pageMarginTop: 10,
    pageMarginLeft: 10,
    pageMarginRight: 10,
    title: '来料检验报告 · IQC-0731',
    scale: 1,
    background: '#fcfdfd',
    color: '#202a35',
    fontSize: 10,
    fontFamily: UI_FONT,
    lineHeight: 1.4,
  },
  pages: [
    {
      id: 'inspection-page-1',
      componentData: [
        component('inspection-kicker', 'RoySimpleText', '报告类型', 'QUALITY CONTROL / IQC', {
          left: 64,
          top: 48,
          width: 250,
          height: 18,
          rotate: 0,
          opacity: 1,
          color: '#567089',
          fontFamily: UI_FONT,
          fontSize: 8,
          fontWeight: '700',
          letterSpacing: '1.4px',
        }),
        component('inspection-title', 'RoySimpleText', '报告标题', '来料检验报告', {
          left: 64,
          top: 72,
          width: 340,
          height: 42,
          rotate: 0,
          opacity: 1,
          color: '#202a35',
          fontFamily: SERIF_FONT,
          fontSize: 26,
          fontWeight: '600',
        }),
        component('inspection-code', 'RoySimpleText', '报告编号', 'IQC-2026-0731-06', {
          left: 478,
          top: 80,
          width: 245,
          height: 24,
          rotate: 0,
          opacity: 1,
          color: '#3b4c5d',
          fontFamily: UI_FONT,
          fontSize: 11,
          fontWeight: '700',
          justifyContent: 'flex-end',
        }),
        component(
          'inspection-rule',
          'RoyLine',
          '标题分隔线',
          null,
          {
            left: 64,
            top: 130,
            width: 659,
            height: 2,
            rotate: 0,
            opacity: 1,
            background: '#202a35',
          },
          'shape',
        ),
        component(
          'inspection-meta-bg',
          'RoyRect',
          '批次信息底框',
          null,
          {
            left: 64,
            top: 158,
            width: 659,
            height: 112,
            rotate: 0,
            opacity: 1,
            background: '#eef2f4',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#cad2d9',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'inspection-meta',
          'RoyText',
          '批次信息',
          '<p><strong>物料名称</strong>　食品级密封圈 / SR-42　　　　　　　　<strong>供应商</strong>　宁波恒密橡塑</p><p><strong>到货批次</strong>　NB-260730-B　　　　　　　　　　　　 <strong>抽样数量</strong>　32 / 1200 PCS</p><p><strong>检验日期</strong>　2026-07-31　　　　　　　　　　　　　 <strong>检验员</strong>　陈屿</p>',
          {
            left: 86,
            top: 172,
            width: 614,
            height: 86,
            rotate: 0,
            opacity: 1,
            color: '#344454',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '1.8',
          },
        ),
        component(
          'inspection-table-head',
          'RoyRect',
          '检验表头',
          null,
          {
            left: 64,
            top: 306,
            width: 659,
            height: 38,
            rotate: 0,
            opacity: 1,
            background: '#2e4154',
            borderWidth: 0,
            borderType: 'solid',
            borderColor: '#2e4154',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'inspection-table-labels',
          'RoySimpleText',
          '检验表头文字',
          '检验项目　　　　　　标准　　　　　　　　　　　结果　　　　　　判定',
          {
            left: 83,
            top: 315,
            width: 620,
            height: 20,
            rotate: 0,
            opacity: 1,
            color: '#f6f8fa',
            fontFamily: UI_FONT,
            fontSize: 9,
            fontWeight: '700',
          },
        ),
        component(
          'inspection-results',
          'RoyText',
          '检验结果',
          '<p>外观　　　　　　　　无裂纹、缺口、污染　　　　　　符合　　　　　　PASS</p><p>关键尺寸　　　　　　42.0 ± 0.15 mm　　　　　　　 42.04 mm　　　　PASS</p><p>硬度　　　　　　　　70 ± 5 Shore A　　　　　　　　 71 Shore A　　　 PASS</p><p>密封性能　　　　　　0.4 MPa / 30 min 无泄漏　　　　无泄漏　　　　　PASS</p><p>包装标识　　　　　　批次、数量与订单一致　　　　　 一致　　　　　　PASS</p>',
          {
            left: 82,
            top: 360,
            width: 620,
            height: 250,
            rotate: 0,
            opacity: 1,
            color: '#273441',
            fontFamily: UI_FONT,
            fontSize: 10,
            lineHeight: '2.35',
          },
        ),
        component(
          'inspection-table-border',
          'RoyRect',
          '检验表格外框',
          null,
          {
            left: 64,
            top: 344,
            width: 659,
            height: 290,
            rotate: 0,
            opacity: 1,
            background: 'transparent',
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#a7b2bc',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'inspection-conclusion-box',
          'RoyRect',
          '结论底框',
          null,
          {
            left: 64,
            top: 678,
            width: 659,
            height: 126,
            rotate: 0,
            opacity: 1,
            background: '#f6f7f4',
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#c8d0d6',
            borderRadius: '0',
          },
          'shape',
        ),
        component(
          'inspection-conclusion',
          'RoyText',
          '检验结论',
          '<p><strong>检验结论</strong></p><p>本批次抽检项目全部符合来料检验规范，可转入合格区并办理入库。</p>',
          {
            left: 88,
            top: 700,
            width: 440,
            height: 80,
            rotate: 0,
            opacity: 1,
            color: '#344454',
            fontFamily: UI_FONT,
            fontSize: 11,
            lineHeight: '1.7',
          },
        ),
        component('inspection-pass', 'RoySimpleText', '合格印章', 'PASS\n合 格', {
          left: 584,
          top: 698,
          width: 105,
          height: 82,
          rotate: -8,
          opacity: 1,
          color: '#f8fafc',
          background: '#19805b',
          fontFamily: UI_FONT,
          fontSize: 16,
          fontWeight: '700',
          lineHeight: '1.25',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }),
        component(
          'inspection-signatures',
          'RoySimpleText',
          '签字栏',
          '检验员　　　　　　　　　复核人　　　　　　　　　质量负责人',
          {
            left: 64,
            top: 872,
            width: 659,
            height: 24,
            rotate: 0,
            opacity: 1,
            color: '#66727e',
            fontFamily: UI_FONT,
            fontSize: 9,
          },
        ),
        component(
          'inspection-footer',
          'RoySimpleText',
          '页脚',
          'FOLIQ / INCOMING QUALITY CONTROL · 01',
          {
            left: 64,
            top: 1020,
            width: 659,
            height: 18,
            rotate: 0,
            opacity: 1,
            color: '#7a8792',
            fontFamily: UI_FONT,
            fontSize: 8,
            letterSpacing: '0.8px',
          },
        ),
      ],
    },
  ],
  dataSource: [],
  dataSet: {},
}

export const PRODUCT_CAPTURE_KEYS = [
  'cold-chain',
  'delivery-note',
  'price-label',
  'inspection-report',
] as const

export type ProductCaptureKey = (typeof PRODUCT_CAPTURE_KEYS)[number]

export const PRODUCT_CAPTURE_TEMPLATES: Record<ProductCaptureKey, TemplateSchema> = {
  'cold-chain': PRODUCT_CAPTURE_TEMPLATE,
  'delivery-note': DELIVERY_NOTE_CAPTURE_TEMPLATE,
  'price-label': PRICE_LABEL_CAPTURE_TEMPLATE,
  'inspection-report': INSPECTION_REPORT_CAPTURE_TEMPLATE,
}
